import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.fabricationJobs.OptimizationPanel.';
  public static readonly optimizeButton = `${Id.prefix}optimizeButton`;
  public static readonly cancelButton = `${Id.prefix}cancelButton`;
  public static readonly lockInButton = `${Id.prefix}lockInButton`;
  public static readonly viewButton = `${Id.prefix}viewButton`;
  public static readonly optimizingToast = `${Id.prefix}optimizingToast`;
  public static readonly optimizationCompletedToast = `${Id.prefix}optimizationCompletedToast`;
  public static readonly optimizationFailedToast = `${Id.prefix}optimizationFailedToast`;
  public static readonly kerfRequiredToast = `${Id.prefix}kerfRequiredToast`;
  public static readonly cancelledToast = `${Id.prefix}cancelledToast`;
  public static readonly lockedInToast = `${Id.prefix}lockedInToast`;
  public static readonly stateInProgress = `${Id.prefix}stateInProgress`;
  public static readonly errorDetailLabel = `${Id.prefix}errorDetailLabel`;
  public static readonly cutplansRowLabel = `${Id.prefix}cutplansRowLabel`;
  public static readonly totalBarCountRowLabel = `${Id.prefix}totalBarCountRowLabel`;
  public static readonly totalBarLengthRowLabel = `${Id.prefix}totalBarLengthRowLabel`;
  public static readonly wasteRowLabel = `${Id.prefix}wasteRowLabel`;
  public static readonly kerfRowLabel = `${Id.prefix}kerfRowLabel`;
  public static readonly kerfAngleRowLabel = `${Id.prefix}kerfAngleRowLabel`;
  public static readonly editKerfTooltip = `${Id.prefix}editKerfTooltip`;
}

export class LangOptimizationPanel {
  private constructor() {}
  public static optimizeButton(): string {
    return interpolate(Id.optimizeButton);
  }
  public static cancelButton(): string {
    return interpolate(Id.cancelButton);
  }
  public static lockInButton(): string {
    return interpolate(Id.lockInButton);
  }
  public static viewButton(): string {
    return interpolate(Id.viewButton);
  }
  public static optimizingToast(): string {
    return interpolate(Id.optimizingToast);
  }
  public static optimizationCompletedToast(): string {
    return interpolate(Id.optimizationCompletedToast);
  }
  public static optimizationFailedToast(errorMessage: string): string {
    return interpolate(Id.optimizationFailedToast, { errorMessage });
  }
  public static kerfRequiredToast(): string {
    return interpolate(Id.kerfRequiredToast);
  }
  public static cancelledToast(): string {
    return interpolate(Id.cancelledToast);
  }
  public static lockedInToast(): string {
    return interpolate(Id.lockedInToast);
  }
  public static stateInProgress(): string {
    return interpolate(Id.stateInProgress);
  }
  public static errorDetailLabel(errorMessage: string): string {
    return interpolate(Id.errorDetailLabel, { errorMessage });
  }
  public static cutplansRowLabel(): string {
    return interpolate(Id.cutplansRowLabel);
  }
  public static totalBarCountRowLabel(): string {
    return interpolate(Id.totalBarCountRowLabel);
  }
  public static totalBarLengthRowLabel(): string {
    return interpolate(Id.totalBarLengthRowLabel);
  }
  public static wasteRowLabel(): string {
    return interpolate(Id.wasteRowLabel);
  }
  public static kerfRowLabel(): string {
    return interpolate(Id.kerfRowLabel);
  }
  public static kerfAngleRowLabel(): string {
    return interpolate(Id.kerfAngleRowLabel);
  }
  public static editKerfTooltip(): string {
    return interpolate(Id.editKerfTooltip);
  }
}

export class OptimizationPanelModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.optimizeButton, 'Optimize', 'Primary button to run optimization');
    register.add(
      Id.cancelButton,
      'Cancel',
      'Button to abort an in-progress optimization run; shown only while the run is InProgress'
    );
    register.add(
      Id.lockInButton,
      'Lock In',
      'Button to lock in a completed optimization and transition the job to Planned'
    );
    register.add(
      Id.viewButton,
      'Download CutPlan Report',
      'Button to open the cutting-plan PDF in a new tab; enabled only when a completed cutting plan is available'
    );
    register.add(Id.optimizingToast, 'Optimizing', 'Toast shown when the user kicks off an optimization run');
    register.add(
      Id.optimizationCompletedToast,
      'Optimization complete',
      'Toast shown when an optimization run completes'
    );
    register.add(
      Id.optimizationFailedToast,
      'Optimization failed: {{errorMessage}}',
      'Toast shown when an optimization run fails'
    );
    register.add(
      Id.kerfRequiredToast,
      'Kerf must be set up before optimizing. Use the gear icon to open Fabricator Settings.',
      'Toast shown when the user clicks Optimize but the straight kerf is still zero'
    );
    register.add(Id.cancelledToast, 'Optimization cancelled', 'Toast shown after a successful Cancel');
    register.add(
      Id.lockedInToast,
      'Optimization locked in. Job is now Planned.',
      'Toast shown after a successful Lock In'
    );
    register.add(
      Id.stateInProgress,
      'In progress',
      'Tooltip on the optimization-running spinner shown next to the Optimize button'
    );
    register.add(Id.errorDetailLabel, '{{errorMessage}}', 'Wrapper for the raw error message in the failed state');
    register.add(
      Id.cutplansRowLabel,
      'Cutplans',
      'Read-only row label showing the number of cutplans produced by the optimization run'
    );
    register.add(
      Id.totalBarCountRowLabel,
      'Total Bars',
      'Read-only row label showing the total number of bars consumed across every cutplan'
    );
    register.add(
      Id.totalBarLengthRowLabel,
      'Total Bar Length',
      'Read-only row label showing the sum of bar lengths across every cutplan'
    );
    register.add(Id.wasteRowLabel, 'Waste', 'Read-only row label showing the waste percentage of the optimization run');
    register.add(Id.kerfRowLabel, 'Straight Kerf', 'Read-only row label for the straight kerf used in optimization');
    register.add(Id.kerfAngleRowLabel, 'Angled Kerf', 'Read-only row label for the angled kerf used in optimization');
    register.add(
      Id.editKerfTooltip,
      'Edit kerf in Fabricator Settings',
      'Tooltip on the gear icon that links to the kerf settings page'
    );
  }
}
